// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="./adobe-target.service.types.d.ts" />
import { Injectable, DOCUMENT, inject } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { ApContentService } from '@ap/content';
import { ApCookieConsentService } from '@ap/cookie-consent';

import { ApPersonalizationService } from '../personalization.service';

import content from './adobe-target.content.json';

export const TARGET_SCRIPT_PATH = 'libs/components/personalization/src/lib/adobe-target/assets/at_v2.6.1.js';
export const VISITOR_SCRIPT_PATH = 'libs/components/personalization/src/lib/adobe-target/assets/visitorapi_v4.4.0.js';

export const VOLKSBANK_ADOBE_ID = 'B54D1CFE533094C60A490D45@AdobeOrg';

@Injectable({ providedIn: 'root' })
export class ApAdobeTargetService implements ApPersonalizationService {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly cookieConsentService = inject(ApCookieConsentService);
    private readonly contentService = inject(ApContentService);

    constructor() {
        this.contentService.addContentItems(content);
    }

    /*
     * Load the adobe target platform for personalization and A/B testing.
     * The platform will only be loaded when the user has consented to tracking
     * and the consent cookie is available.
     *
     * This function should only be called once on channel level
     * i.e. scripts are loaded by retail or mijn-omgeving, and should not be loaded
     * by each process within that channel individually.
     *
     * Applications that run standalone should initialize the tracking platform
     * individually to enable A/B testing.
     */
    public loadPlatform(): Observable<boolean> {
        if (!this.personalizationAllowed()) {
            return of(false);
        }
        return this.loadScript(VISITOR_SCRIPT_PATH).pipe(
            tap(() => {
                // @ts-ignore
                window.Visitor.getInstance(VOLKSBANK_ADOBE_ID, {
                    trackingServer: this.contentService.get('tracking.server'),
                    trackingServerSecure: this.contentService.get('tracking.server.secure')
                });
                // @ts-ignore
                window.targetPageParams = () => ({
                    at_property: this.contentService.get('tracking.target.page.params')
                });
            }),
            switchMap(() => this.loadScript(TARGET_SCRIPT_PATH)),
            catchError((err) => {
                console.error(err);
                throw err;
            })
        );
    }

    private loadScript(scriptSrc: string): Observable<boolean> {
        if (!this.scriptIsLoaded(scriptSrc)) {
            return this.insertScript(scriptSrc).pipe(map(() => true));
        }
        return of(true);
    }

    private scriptIsLoaded(scriptSrc: string): boolean {
        return !!this.document.querySelector(`script[src="${scriptSrc}"]`);
    }

    private insertScript(scriptSrc: string): Observable<void> {
        const loaded = new Subject<void>();
        const scriptTag = this.document.createElement('script');
        scriptTag.setAttribute('async', '');
        scriptTag.src = scriptSrc;
        scriptTag.onload = () => loaded.next();
        this.document.body.appendChild(scriptTag);
        return loaded.pipe(take(1));
    }

    private personalizationAllowed(): boolean {
        return this.cookieConsentService.getConsent().personalisation;
    }
}
